/**
 * Discount codes API.
 *
 * CRUD lives in campaignsAPI.ts (legacy — predates this feature being wired
 * into checkout flows). This module re-exports those plus adds the runtime
 * pieces — master toggle, validate, redeem — that the registration wizard
 * and admin UI actually use.
 */
import axios from "./axiosInstance";
import config from "./config";

export {
  getDiscountCodes,
  createDiscountCode,
  updateDiscountCode,
  deleteDiscountCode,
} from "./campaignsAPI";
export type { DiscountCode } from "./campaignsAPI";

const BASE_URL = config.BASE_URL;

export type DiscountSurface = "registration" | "manual_invoice" | "store_events";

export interface ValidateResponse {
  success: boolean;
  valid: boolean;
  error?: string;
  code?: {
    id: string;
    code: string;
    type: "percentage" | "fixed";
    value: number;
    description: string | null;
  };
  discount_amount?: number | null;
}

const authHeaders = (token: string | null) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const getDiscountCodesMasterToggle = (
  token: string,
): Promise<{ success: boolean; master_enabled?: boolean; error?: string }> =>
  axios
    .get(`${BASE_URL}/discount-codes/master-toggle`, { headers: authHeaders(token) })
    .then((res) => res.data)
    .catch((err) => ({
      success: false,
      error: err.response?.data?.error || err.message,
    }));

export const setDiscountCodesMasterToggle = (
  token: string,
  enabled: boolean,
): Promise<{ success: boolean; master_enabled?: boolean; error?: string }> =>
  axios
    .post(
      `${BASE_URL}/discount-codes/master-toggle`,
      { enabled },
      { headers: authHeaders(token) },
    )
    .then((res) => res.data)
    .catch((err) => ({
      success: false,
      error: err.response?.data?.error || err.message,
    }));

/**
 * Whether this club accepts discount codes (the master switch), for the
 * checkout forms. null when the answer could not be had: the form then shows
 * the code box rather than hide it on a failed request.
 */
let enabledAnswer: Promise<boolean | null> | null = null;
export const getDiscountCodesEnabled = (): Promise<boolean | null> => {
  if (!enabledAnswer) {
    enabledAnswer = axios
      .get(`${BASE_URL}/public/discount-codes/enabled`, { headers: authHeaders(null) })
      .then((res) => (res?.data?.success ? !!res.data.enabled : null))
      .catch(() => null)
      .then((answer) => {
        // A failed answer is asked again next time; a real one is kept for the page.
        if (answer === null) enabledAnswer = null;
        return answer;
      });
  }
  return enabledAnswer;
};
/** Test seam: forget the remembered answer. */
export const _resetDiscountCodesEnabled = () => {
  enabledAnswer = null;
};

// Public — used from registration wizard / store / events without staff auth.
// `memberEmail` (optional) lets the server enforce max_uses_per_member.
// Pass it whenever the calling surface knows the customer's email — wizard,
// manual invoice, store/events checkout. Omit when truly anonymous.
export const validateDiscountCode = (
  code: string,
  surface: DiscountSurface,
  subtotal?: number,
  memberEmail?: string,
): Promise<ValidateResponse> =>
  axios
    .post(
      `${BASE_URL}/public/discount-codes/validate`,
      { code, surface, subtotal, member_email: memberEmail || undefined },
      { headers: authHeaders(null) },
    )
    .then((res) => res.data)
    .catch((err) => ({
      success: false,
      valid: false,
      error: err.response?.data?.error || err.message,
    }));

/**
 * @deprecated Redemption now fires server-side at the source-of-truth
 * payment-success event (Stripe webhook for orders/events/camps/clinics,
 * generateSimpleInvoice for manual_invoice, completeRegistration for the
 * registration wizard). The frontend no longer needs to call this — keep
 * the function for compatibility but prefer letting the server handle it
 * so a tab close mid-flow doesn't lose the used_count bump.
 */
export const redeemDiscountCode = (
  code: string,
  surface: DiscountSurface,
): Promise<{ success: boolean; error?: string }> =>
  axios
    .post(
      `${BASE_URL}/public/discount-codes/redeem`,
      { code, surface },
      { headers: authHeaders(null) },
    )
    .then((res) => res.data)
    .catch((err) => ({
      success: false,
      error: err.response?.data?.error || err.message,
    }));
